import React, { useCallback, useEffect, useState } from 'react'
import { useLazyGetPredictionDataQuery } from '../../../../core/api/BuffetDishApi'
import { DishItem } from './DishItem'
import { useTranslation } from 'react-i18next'
import {
  IBuffetDishWithImplInfoDto,
  IPredictDataDto,
} from '../../../../core/api/dto/BuffetDishDto'
import { AppScrollWrapperNew } from '../../../../widgets/AppScrollWrapper/AppScrollWrapper'
import { PredictionDishPopup } from './PredictionDishPopup/PredictionDishPopup'
import { useSearchParams } from 'react-router-dom'
import { handleBase64Image } from '../../../../utils/base64ToBlob'
import { ModalMobile } from '../../../../shared/ui/ModalMobile/ModalMobile'
import { useGetScalesReadingsQuery } from '../../../../core/api/microservice/ScalesApi'
import { useAppSelector } from '../../../../core/store/store'
import { ScaleAI } from './ScaleAi/ScaleAI'
import { useNextStep } from './hooks/useNextStep'
import { useAiAssistantMode } from './hooks/useAiAssistantMode'
import { useStableWeightWs } from './hooks/useStableWeightWs'

interface PredictionDataProps {
  isIds: boolean
}

export const PredictionData = ({ isIds }: PredictionDataProps) => {
  const { t } = useTranslation()
  const { onContinueHandler } = useNextStep()
  const [predictionDataLock, setPredictionDataLock] = useState(false)
  const [isOpenPopup, setIsOpenPopup] =
    useState<IBuffetDishWithImplInfoDto | null>(null)
  const [predictionData, setPredictionData] = useState<IPredictDataDto | null>(
    null,
  )
  const [predictionPopup, setPredictionPopup] = useState(true)

  useGetScalesReadingsQuery()

  const [latestStableReadingG, setLatestStableReadingG] = useState(0)

  const latestReading = useAppSelector((state) => state.scalesReading)
  const currentScalesReadingValueKg = latestReading.reading.value / 1000

  const [getPredictionData, { isError }] = useLazyGetPredictionDataQuery()
  const [searchParams] = useSearchParams()
  const orderIds = searchParams.get('ids')?.split(',')
  const onOpenPopup = (dish: IBuffetDishWithImplInfoDto) => {
    setIsOpenPopup(dish)
  }
  const onClosePopup = () => setIsOpenPopup(null)

  useAiAssistantMode({
    predictionData,
    isOpenPopup: !!isOpenPopup,
    setIsOpenPopup,
  })

  const setPredictionDataHandler = useCallback(
    (clear?: boolean) => {
      if (clear) {
        setPredictionData(null)
        setPredictionDataLock(false)
      } else {
        getPredictionData()
          .unwrap()
          .then((data) => setPredictionData(data))
          .catch(() => setPredictionData(null))
          .finally(() => {
            setPredictionDataLock(false)
          })
      }
    },
    [getPredictionData],
  )

  useStableWeightWs(
    latestStableReadingG,
    setLatestStableReadingG,
    predictionDataLock,
    setPredictionDataLock,
    setPredictionDataHandler,
    latestReading,
  )

  useEffect(() => {
    if (latestReading.isWsOpen) {
      console.debug('PredictionData: ws open')
    }
  }, [latestReading.isWsOpen])

  useEffect(() => {
    if (
      ((isError || currentScalesReadingValueKg === 0.0) && isOpenPopup) ||
      (isOpenPopup &&
        !predictionData?.results?.find(
          (item) => item.dish.id === isOpenPopup.id,
        ))
    ) {
      setIsOpenPopup(null)
    }
  }, [isError, predictionData])

  if (!!predictionData && isIds && currentScalesReadingValueKg >= 0.005) {
    return (
      <>
        {isOpenPopup && (
          <PredictionDishPopup
            dish={isOpenPopup}
            onClosePopup={onClosePopup}
            onContinue={onContinueHandler}
            setPredictionData={setPredictionData}
            imageFromCamera={
              predictionData?.image
                ? URL.createObjectURL(
                    handleBase64Image(
                      predictionData?.image,
                      'фото с фотокассы',
                    ),
                  )
                : ''
            }
            orderIds={orderIds}
          />
        )}
        <ModalMobile
          onToggle={() => setPredictionPopup((prev) => !prev)}
          onClose={() => {}}
          isOpen={predictionPopup}
        >
          <div className="mt-15 mb-20">
            <div className="title-wrap">
              <div className="title-ico">
                <i className="an-ico an-ico-stars" />
                <span>{t('Блюдо распознанное ИИ помощником')}</span>
              </div>
              <div
                className="ico-block"
                onClick={() => setPredictionPopup(false)}
              >
                <i className="an-ico an-ico-close" />
              </div>
            </div>
            <div className="plan-menu-control-manager-page auto-height ai-line mt-10">
              <AppScrollWrapperNew>
                {predictionData?.results.map((dish, index) => (
                  <div
                    key={`${dish.dish.id}d-in-m-${index}`}
                    style={{ padding: 10 }}
                  >
                    <ScaleAI accuracy={dish.accuracy} id={dish.dish.id} />
                    <DishItem
                      key={`${dish.dish.id}`}
                      dish={dish.dish as IBuffetDishWithImplInfoDto}
                      onOpenPopup={onOpenPopup}
                    />
                  </div>
                ))}
              </AppScrollWrapperNew>
            </div>
          </div>
        </ModalMobile>
      </>
    )
  }

  return null
}
